import { FEATURE_GUID_ALIASES } from './feature-guid-aliases';

import type { Feature, FeatureGroup } from '../types';

/**
 * A map from FeatureGroup GUID -> the set of all descendant leaf feature GUIDs.
 * Nested groups are fully expanded, so a single group GUID resolves all features
 * at any depth below it.
 */
export type FeatureGroupRegistry = Map<string, Set<string>>;

/**
 * Builds a FeatureGroupRegistry from flat arrays of groups and features.
 *
 * Each group entry in the registry holds the complete set of leaf feature GUIDs
 * reachable from that group — including features nested inside child groups.
 * Circular parent references are broken automatically.
 *
 * @example
 * // ASSETS_GROUP -> { colors:read, colors:update, images:create, ... }
 * // ASSETS_TYPOGRAPHY_GROUP (child of ASSETS_GROUP) -> { typography:create, typography:read, ... }
 */
export function buildFeatureGroupRegistry(groups: FeatureGroup[], features: Feature[]): FeatureGroupRegistry {
  const registry: FeatureGroupRegistry = new Map();

  function collectDescendants(groupGuid: string): Set<string> {
    const cached = registry.get(groupGuid);
    if (cached) return cached;

    // Register early to break circular parent references
    const descendants = new Set<string>();
    registry.set(groupGuid, descendants);

    // Collect direct leaf feature children
    for (const feature of features) {
      if (feature.parentGuid === groupGuid) {
        descendants.add(feature.guid);
      }
    }

    // Recurse into child groups and merge their descendants
    for (const group of groups) {
      if (group.parentGuid === groupGuid) {
        const childDescendants = collectDescendants(group.guid);
        for (const guid of childDescendants) {
          descendants.add(guid);
        }
      }
    }

    return descendants;
  }

  for (const group of groups) {
    collectDescendants(group.guid);
  }

  return registry;
}

/**
 * Expands a mixed list of feature GUIDs and FeatureGroup GUIDs into a flat Set of
 * leaf feature GUIDs only.
 *
 * - Retired GUIDs in FEATURE_GUID_ALIASES expand to their live replacements first.
 * - Group GUIDs are replaced by all their descendant leaf features (recursively).
 * - Leaf feature GUIDs pass through unchanged.
 *
 * @example
 * // If ASSETS_GROUP.guid is in the input, all asset feature GUIDs are in the output
 * expandGuids([ASSETS_GROUP.guid, TEAM_FEATURES.READ.guid], registry)
 * // -> Set { 'assets:colors:read guid', ..., 'team:read guid' }
 */
export function expandGuids(guids: string[], registry: FeatureGroupRegistry): Set<string> {
  const result = new Set<string>();

  for (const guid of guids) {
    const aliases = FEATURE_GUID_ALIASES[guid];
    if (aliases) {
      const expandedAliases = expandGuids([...aliases], registry);
      for (const featureGuid of expandedAliases) {
        result.add(featureGuid);
      }
    } else {
      const groupDescendants = registry.get(guid);
      if (groupDescendants) {
        for (const featureGuid of groupDescendants) {
          result.add(featureGuid);
        }
      } else {
        result.add(guid);
      }
    }
  }

  return result;
}
